import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { auditVocabularyService } from '../../services/auditVocabularyService';
import './FindingsCRUD.css';

const DEBOUNCE_MS = 300;

const FindingsCRUD = ({ hospitalId, category, value = [], onChange, placeholder, label, disabled }) => {
  const { t } = useTranslation(['qms', 'common']);
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(
    async (term) => {
      if (!hospitalId) return;
      try {
        const list = await auditVocabularyService.getSuggestions(hospitalId, category, term);
        setSuggestions(list);
      } catch (e) {
        console.error('[FindingsCRUD] suggestions', e);
        setSuggestions([]);
      }
    },
    [hospitalId, category]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const scheduleSuggest = (term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(term);
    }, DEBOUNCE_MS);
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    setShowSuggest(true);
    scheduleSuggest(v);
  };

  const handleFocus = () => {
    setShowSuggest(true);
    scheduleSuggest(input);
  };

  const addText = (text) => {
    const t0 = (text || '').trim();
    if (!t0) return;
    onChange?.([...value, t0]);
    auditVocabularyService.upsertEntry(hospitalId, category, t0).catch((e) => console.error(e));
    setInput('');
    setSuggestions([]);
    setShowSuggest(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    addText(input);
  };

  return (
    <div className="findings-crud">
      {label && <label className="form-label">{label}</label>}
      <form className="findings-crud-form" onSubmit={handleSubmit}>
        <div className="findings-crud-input-wrap">
          <input
            type="text"
            className="form-input"
            value={input}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={showSuggest && suggestions.length > 0}
          />
          {showSuggest && suggestions.length > 0 && !disabled && (
            <ul className="findings-crud-suggest" role="listbox">
              {suggestions.map((s, i) => (
                <li key={`${s}-${i}`} role="option">
                  <button type="button" className="findings-crud-suggest-item" onClick={() => addText(s)}>
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="submit"
          className="btn btn-secondary findings-crud-add-btn"
          disabled={disabled || !input.trim()}
        >
          {t('common:add')}
        </button>
      </form>
      {value.length === 0 ? (
        <p className="findings-crud-empty">{t('qms:noFindingsYet')}</p>
      ) : (
        <ul className="findings-crud-list">
          {value.map((item, idx) => (
            <li key={`${idx}-${item.slice(0, 20)}`} className="findings-crud-item">
              <span>{item}</span>
              {!disabled && (
                <button
                  type="button"
                  className="findings-crud-remove"
                  onClick={() => onChange?.(value.filter((_, j) => j !== idx))}
                  aria-label={t('common:remove')}
                >
                  <X size={16} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FindingsCRUD;
